// frontend/src/config/debug.ts
import { errorStatusForLog } from '../lib/apiError';
// Centralized debug configuration for the entire application

/**
 * Debug configuration object
 * Set to false to disable logs in production
 */
export const DEBUG_CONFIG = {
  // Core modules
  API: false,              // API calls and responses
  AUTH: false,             // Authentication flow
  ROUTES: false,           // Route protection and navigation

  // Features
  TASKS: false,            // Task management
  PROPERTIES: false,       // Property management
  BOOKINGS: false,         // Booking management
  EMPLOYEES: false,        // Employee management

  // Advanced
  VERBOSE: false,          // Detailed API logs (headers, full responses)
  PERFORMANCE: false,      // Performance timing logs

  // Special
  FORCE_ALL: false         // Override all settings (for emergency debugging)
};

// Cache the original console methods
const originalConsoleLog = console.log;
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;
const originalConsoleGroup = console.group;
const originalConsoleGroupEnd = console.groupEnd;
const originalConsoleTable = console.table;

/**
 * Check if debug is enabled for a category
 */
const isDebugEnabled = (category: keyof typeof DEBUG_CONFIG): boolean => {
  if (DEBUG_CONFIG.FORCE_ALL) return true;

  // ✅ DISABLE IN PRODUCTION unless forced
  if (import.meta.env.PROD) return false;

  return DEBUG_CONFIG[category] || false;
};

// Silence normal console methods globally in production or when VERBOSE is disabled.
// This silences the 1000+ raw console.log calls scattered throughout the codebase.
if (import.meta.env.PROD || (!DEBUG_CONFIG.VERBOSE && !DEBUG_CONFIG.FORCE_ALL)) {
  console.log = () => {};
  console.group = () => {};
  console.groupEnd = () => {};
  console.table = () => {};
}

/**
 * Debug logging function
 * Only logs if the category is enabled
 * 
 * @example
 * debugLog('API', '🌐 Making request to /api/tasks');
 */
export const debugLog = (category: keyof typeof DEBUG_CONFIG, ...args: any[]) => {
  if (isDebugEnabled(category)) {
    originalConsoleLog(...args);
  }
};

/**
 * Debug error function
 * Errors are critical and always logged to the console.
 */
export const debugError = (category: keyof typeof DEBUG_CONFIG, ...args: any[]) => {
  originalConsoleError(...args);
};

/**
 * Debug warn function
 * Warnings are important and always logged to the console.
 */
export const debugWarn = (category: keyof typeof DEBUG_CONFIG, ...args: any[]) => {
  originalConsoleWarn(...args);
};

/**
 * Performance timing helper
 * Measures execution time of a function
 * 
 * @example
 * await debugTimer('API', 'Fetching tasks', async () => {
 *   await api.getTasks();
 * });
 */
export const debugTimer = async <T>(
  category: keyof typeof DEBUG_CONFIG,
  label: string,
  fn: () => Promise<T>
): Promise<T> => {
  if (!isDebugEnabled('PERFORMANCE') && !isDebugEnabled(category)) {
    return await fn();
  }

  const startTime = performance.now();
  debugLog(category, '⏱️  Starting: …');

  try {
    const result = await fn();
    const endTime = performance.now();
    const duration = (endTime - startTime).toFixed(2);

    debugLog(category, '✅ Completed: … (…ms)');

    return result;
  } catch (error) {
    const endTime = performance.now();
    const duration = (endTime - startTime).toFixed(2);

    debugError(category, '❌ Failed: … (…ms)', errorStatusForLog(error));
    throw error;
  }
};

/**
 * Group console logs for better organization
 * 
 * @example
 * debugGroup('API', 'User Login', () => {
 *   debugLog('API', 'Email:', email);
 *   debugLog('API', 'Remember me:', rememberMe);
 * });
 */
export const debugGroup = (
  category: keyof typeof DEBUG_CONFIG,
  label: string,
  fn: () => void
) => {
  if (!isDebugEnabled(category)) return;

  originalConsoleGroup(label);
  fn();
  originalConsoleGroupEnd();
};

/**
 * Debug table for structured data
 * 
 * @example
 * debugTable('API', 'Response Data', responseData);
 */
export const debugTable = (
  category: keyof typeof DEBUG_CONFIG,
  label: string,
  data: any
) => {
  if (!isDebugEnabled(category)) return;

  originalConsoleLog(label);
  originalConsoleTable(data);
};

// Export configuration for external access
export default DEBUG_CONFIG;